/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/prefer-default-export */
/* eslint-disable no-undef */
import { useEffect, useRef, useLayoutEffect, useContext } from 'react';
import { SyncContext } from 'contexts/sync';
import { useDispatch } from 'react-redux';
import db from 'database';
import { throttle } from 'lodash';

const isBrowser = typeof window !== 'undefined';

export function useEditableContext(enabled = true) {
  const { addEditableContext, removeEditableContext } = useContext(SyncContext);
  useEffect(() => {
    if (enabled) {
      addEditableContext();
    }
    return () => removeEditableContext();
  }, [enabled]);
}

export function useElementScroll(handler, element, deps = [element, handler]) {
  const target = element && element.current ? element.current : window;
  useLayoutEffect(() => {
    target.addEventListener('onscroll', handler);
    return () => target.removeEventListener('onscroll', handler);
  }, deps);
}

function getScrollPosition({ element, useWindow }) {
  if (!isBrowser) return { x: 0, y: 0 };

  const target = element ? element.current : document.body;
  const position = target.getBoundingClientRect();

  return useWindow
    ? { x: window.scrollX, y: window.scrollY }
    : { x: position.left, y: position.top };
}

export function useScrollPosition(effect, deps, element, useWindow, wait) {
  const position = useRef(getScrollPosition({ useWindow }));

  let throttleTimeout = null;

  const callBack = () => {
    const currPos = getScrollPosition({ element, useWindow });
    effect({ prevPos: position.current, currPos });
    position.current = currPos;
    throttleTimeout = null;
  };

  useLayoutEffect(() => {
    const handleScroll = () => {
      if (wait) {
        if (throttleTimeout === null) {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          throttleTimeout = setTimeout(callBack, wait);
        }
      } else {
        callBack();
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, deps);
}

export function useOnClickOutside(ref, handler) {
  useEffect(
    () => {
      const listener = (event) => {
        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }

        handler(event);
      };

      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);

      return () => {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, handler],
  );
}

export function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();

  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes

  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

/**
 * When mounting a component binds to database updates and
 * action is dispatched when the database is updated
 */
export function useDatabaseSync(table, action) {
  const dispatch = useDispatch();
  const throttleFunction = useRef(
    throttle(
      () => {
        dispatch(action());
      },
      500,
      { leading: false, trailing: true },
    ),
  );
  const blockListener = useRef(false);
  useEffect(() => {
    db.on('changes', (changes) => {
      if (
        changes.find(
          (c) => c.table === table && c.type !== 3,
        ) && !blockListener.current
      ) {
        throttleFunction.current();
      }
    });
    return () => {
      blockListener.current = true;
    };
  }, []);
}

/**
 * @type {<T extends Array<any>>(effect: (p: T) => void, depArray: T) => void}
 */
export function useEffectWithPrevious(effect, dependencyArray) {
  const internalRef = useRef(dependencyArray);

  useEffect(() => {
    effect(internalRef.current);
    internalRef.current = dependencyArray;
  }, dependencyArray);
}
